// src/components/Modals/FolderConnectModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStorage } from "@/contexts/StorageContext";
import { useFeedback } from "@/contexts/FeedbackContext";

export default function FolderConnectModal({ open, onClose }) {
  const {
    mode, hasFolder, baseDirName, workspaceName,
    isDriveLinked, isDriveAuthed,
    linkLocalFolder: pickFolder, connectDrive, healthCheck, forgetRememberedFolder,
    hasWorkspace, activeWorkspace,
    driveConfig, bootstrappingDrive,
    restorePhase, restoreProgress, receiveSource,
  } = useStorage();
  const { play, buzz } = useFeedback();

  const fsAccess = useMemo(() => !!window.showDirectoryPicker, []);
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const first = dialogRef.current?.querySelector?.("button,[href],input,select,textarea");
    first?.focus?.();

    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        play("tap"); buzz("light");
        if (hasFolder || isDriveLinked) onClose?.();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
        e.preventDefault();
        // prefer Drive connect if not linked; else local
        if (!isDriveLinked) onConnectDrive();
        else if (!hasFolder) onLinkLocal();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, hasFolder, isDriveLinked]);

  if (!open) return null;

  const canClose = hasFolder || isDriveLinked;

  async function run(fn) {
    if (busy) return;
    setBusy(true);
    try { await fn(); play("success"); buzz("light"); }
    catch (e) {
      play("error"); buzz("error");
      alert(e?.message || "Operation failed.");
    } finally { setBusy(false); }
  }

  async function onLinkLocal() {
    await run(async () => { await pickFolder(); onClose?.(); });
  }
  async function onConnectDrive() {
    await run(async () => { await connectDrive(); onClose?.(); });
  }
  async function onHealth() {
    await run(async () => {
      const res = await healthCheck();
  alert(res?.ok ? "Storage OK" : "Not linked yet.");
    });
  }

  async function onForgetRemembered() {
    await run(async () => {
      const ok = await (forgetRememberedFolder?.());
      alert(ok ? "Forgot remembered local folder for this device." : "Nothing to forget or operation failed.");
    });
  }

  // Overlay shows only for manual Receive operations
  const isBootstrapping = (receiveSource === "manual") && (restorePhase && restorePhase !== "idle");

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="folder-connect-title"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "grid", placeItems: "center", zIndex: 99999 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && canClose) onClose?.(); }}
    >
      <div
        ref={dialogRef}
        style={{
          width: isBootstrapping ? 420 : 520, maxWidth: "92vw", padding: 20, borderRadius: 16,
          border: "1px solid rgba(255,255,255,0.12)", background: "rgba(22,22,24,0.85)", color: "#fff",
          boxShadow: "0 10px 30px rgba(0,0,0,0.35)", backdropFilter: "blur(12px)",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {isBootstrapping ? (
          <>
            <h3 id="folder-connect-title" style={{ margin: "0 0 8px 0" }}>Loading your studio from Drive…</h3>
            <p style={{ margin: "0 0 12px 0", opacity: 0.9, fontSize: 14 }}>
              Restoring workspace from {driveConfig?.linkedEmail || "Google Drive"}. This will finish shortly.
            </p>
            <div className="mt-4 w-full bg-black/30 rounded-full h-2 overflow-hidden">
              <div
                className="h-2 rounded-full bg-orange-400 transition-[width] duration-300"
                style={{ width: `${restoreProgress || 5}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-neutral-200">
              {restorePhase === "starting" && "Connecting to Google Drive…"}
              {restorePhase === "listing" && "Finding your Mudo files…"}
              {restorePhase === "downloading" && "Downloading studio data…"}
              {restorePhase === "hydrating" && "Applying studio settings…"}
              {restorePhase === "done" && "Done! Your studio is ready."}
              {restorePhase === "error" && "Something went wrong. You can try Receive again from Studio Settings."}
            </p>
          </>
        ) : (
          <>
            <h3 id="folder-connect-title" style={{ margin: "0 0 8px 0" }}>Connect your studio storage</h3>
            <p style={{ margin: "0 0 12px 0", opacity: 0.9, fontSize: 14 }}>
              {hasWorkspace 
                ? "Your workspace is connected. You can switch to a different workspace below."
                : "Choose where Mudo stores and backs up your data. No workspace = empty app."}
            </p>
          </>
        )}

        {/* Current Workspace Status */}
        {hasWorkspace && activeWorkspace && (
          <div style={{ 
            marginBottom: 12, 
            padding: "8px 12px", 
            borderRadius: 8, 
            backgroundColor: "rgba(34, 197, 94, 0.1)",
            border: "1px solid rgba(34, 197, 94, 0.3)",
            fontSize: 13
          }}>
            <strong>✓ Active Workspace:</strong> {activeWorkspace.type === "drive" ? "Google Drive" : "Local Folder"}
            {activeWorkspace.name && ` (${activeWorkspace.name})`}
          </div>
        )}

        {/* Status pills */}
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <Pill ok={isDriveLinked && isDriveAuthed}>
            Drive: {isDriveLinked ? (workspaceName || "Linked") : "Not linked"}
          </Pill>
          <Pill ok={hasFolder}>
            Local: {hasFolder ? `Linked (${baseDirName})` : "Not linked"}
          </Pill>
          <Pill ok={!!mode}>Mode: {mode || "—"}</Pill>
        </div>

        {!isBootstrapping && (
          <div style={{ display: "grid", gap: 10 }}>
            <button
              onClick={onConnectDrive}
              disabled={busy}
              className="w-full rounded-lg border border-blue-400/30 bg-blue-400/20 px-3 py-2 text-left disabled:opacity-60"
              title="Create and link /Mudo on your Google Drive"
            >
              {busy ? "Connecting to Google Drive…" : "Connect Google Drive (/Mudo)"}
            </button>

            <button
              onClick={onLinkLocal}
              disabled={!fsAccess || busy}
              title={fsAccess ? "Pick a local folder" : "Your browser doesn't support folder linking"}
              className={`w-full rounded-lg px-3 py-2 text-left ${fsAccess ? "border border-white/20 bg-white/10" : "border border-white/10 bg-white/5 opacity-60 cursor-not-allowed"}`}
            >
              {busy ? "Linking folder…" : "Link Local Synced Folder (Drive/Dropbox)"}
            </button>

            <div className="flex gap-2 mt-1">
              <button
                onClick={onHealth}
                disabled={busy}
                className="rounded border border-white/20 bg-transparent px-3 py-1.5 text-sm disabled:opacity-60"
                title="Check connectivity, auth, and folder handles"
              >
                Run health check
              </button>
              <button
                onClick={onForgetRemembered}
                disabled={busy}
                className="rounded border border-white/20 bg-transparent px-3 py-1.5 text-sm disabled:opacity-60"
                title="Forget the remembered local folder on this device"
              >
                Forget remembered folder
              </button>
              {canClose && (
                <button
                  onClick={() => onClose?.()}
                  className="rounded border border-white/20 bg-transparent px-3 py-1.5 text-sm"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Pill({ ok, children }) {
  const cls = ok
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/30"
    : "bg-red-500/10 text-red-300 border-red-400/30";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs ${cls}`}>
      {children}
    </span>
  );
}
